// Word-level diff for permission cards: what a change removes and adds, shown
// inline (struck through / highlighted) instead of two full copies of the text.

const MAX_TOKENS = 2000;

// Words, runs of whitespace, and punctuation on its own, so "HEP." -> "HEP as
// prescribed." marks only the added words
const tokenize = (text) => String(text ?? '').split(/(\s+|[.,;:!?()])/).filter((token) => token !== '');

function merge(parts) {
  const merged = [];
  parts.forEach((part) => {
    const last = merged[merged.length - 1];
    if (last && last.type === part.type) last.text += part.text;
    else merged.push({ ...part });
  });
  return merged;
}

/**
 * [{ type: 'same' | 'removed' | 'added', text }] turning `before` into `after`.
 * Very long texts skip the word-by-word pass and show as removed then added.
 */
export function diffWords(before, after) {
  const a = tokenize(before);
  const b = tokenize(after);
  if (a.length === 0) return b.length ? [{ type: 'added', text: b.join('') }] : [];
  if (b.length === 0) return [{ type: 'removed', text: a.join('') }];
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    return [{ type: 'removed', text: a.join('') }, { type: 'added', text: b.join('') }];
  }

  // Longest common subsequence of tokens, filled from the end
  const rows = a.length + 1;
  const cols = b.length + 1;
  const lcs = new Uint16Array(rows * cols);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i * cols + j] =
        a[i] === b[j] ? lcs[(i + 1) * cols + j + 1] + 1 : Math.max(lcs[(i + 1) * cols + j], lcs[i * cols + j + 1]);
    }
  }

  const parts = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      parts.push({ type: 'same', text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[(i + 1) * cols + j] >= lcs[i * cols + j + 1]) {
      parts.push({ type: 'removed', text: a[i] });
      i += 1;
    } else {
      parts.push({ type: 'added', text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) parts.push({ type: 'removed', text: a[i++] });
  while (j < b.length) parts.push({ type: 'added', text: b[j++] });

  // Whitespace between two changes belongs to the change, not to "same"
  const tidy = parts.map((part, index) => {
    if (part.type !== 'same' || part.text.trim()) return part;
    const prev = parts[index - 1];
    const next = parts[index + 1];
    return prev && next && prev.type !== 'same' && next.type !== 'same' ? { ...part, type: next.type } : part;
  });
  return merge(tidy);
}

/** Whitespace-insensitive equality: a "change" that only reflows text is no change. */
export const sameText = (left, right) =>
  String(left ?? '').replace(/\s+/g, ' ').trim() === String(right ?? '').replace(/\s+/g, ' ').trim();
